import * as m from "@app/paraglide/messages.js";
import { registerInlineEditor } from "@svar-ui/svelte-grid";
import {
    DEFAULT_TABLE_COLUMN_ORDER,
    DEFAULT_TABLE_COLUMN_VISIBILITY,
    mergeTableColumnOrder,
} from "@app/store/editorStore.svelte.js";
import { isTableColumnAllowed } from "@app/store/permissionStore.svelte.js";
import AlignCell from "./AlignCell.svelte";
import CheckCell from "./CheckCell.svelte";
import ErrorCell from "./ErrorCell.svelte";
import HPosCell from "./HPosCell.svelte";
import QuillTextCell from "./QuillTextCell.svelte";
import TagsCell from "./TagsCell.svelte";
import TextareaEditor from "./TextareaEditor.svelte";
import TextCell from "./TextCell.svelte";
import TcCell from "./TcCell.svelte";
import VPosCell from "./VPosCell.svelte";

registerInlineEditor("textarea", TextareaEditor);

/** Column defs + HeaderMenu metadata for the events table */
const STYLE_OPTIONS = [
    { id: "Pop-On", label: "Pop-On" },
    { id: "Paint-On", label: "Paint-On" },
    { id: "Roll-Up 2", label: "Roll-Up 2" },
    { id: "Roll-Up 3", label: "Roll-Up 3" },
    { id: "Roll-Up 4", label: "Roll-Up 4" },
];

const FORCED_OPTIONS = [
    { id: "true", label: "Yes" },
    { id: "false", label: "No" },
];

const APPROVED_OPTIONS = [
    { id: "true", label: "Approved" },
    { id: "false", label: "Failed" },
    { id: "undefined", label: "Needs Review" },
];

/**
 * Columns that appear in the HeaderMenu (all except # which stays fixed).
 * Values of `true` mean the column is listed in the menu.
 */
export function getHeaderMenuColumns() {
    const columns = {
        errors: true,
        track: true,
        start: true,
        end: true,
        duration: true,
        cps: true,
        alignment: true,
        xPos: true,
        yPos: true,
        style: true,
        text: true,
        original: true,
        speakers: true,
        forced: true,
        approved: true,
        notes: true,
        wpm: true,
        reply: true,
        notesCheck: true,
        replyCheck: true,
        voice: true,
        recipients: true,
        tags: true,
    };
    return Object.fromEntries(
        Object.entries(columns).filter(([id]) => isTableColumnAllowed(id)),
    );
}

/**
 * Human-readable labels for the Columns settings picker.
 * @returns {Record<string, string>}
 */
export function getColumnLabels() {
    return {
        number: m.editor_table_column_number(),
        errors: m.editor_table_column_errors(),
        track: m.editor_table_column_track(),
        start: m.editor_table_column_start(),
        end: m.editor_table_column_end(),
        duration: m.editor_table_column_duration(),
        cps: m.editor_table_column_cps(),
        alignment: m.editor_table_column_align(),
        xPos: m.editor_table_column_h(),
        yPos: m.editor_table_column_v(),
        style: m.editor_table_column_style(),
        text: m.editor_table_column_text(),
        original: m.editor_table_column_original(),
        speakers: m.editor_table_column_speakers(),
        forced: m.editor_table_column_forced(),
        approved: m.editor_table_column_approved(),
        notes: m.editor_table_column_notes(),
        wpm: m.editor_table_column_wpm(),
        reply: m.editor_table_column_reply(),
        notesCheck: m.editor_table_column_notes_check(),
        replyCheck: m.editor_table_column_reply_check(),
        voice: m.editor_table_column_voice(),
        recipients: m.editor_table_column_recipients(),
        tags: m.editor_table_column_tags(),
    };
}

/**
 * Build SVAR Grid column definitions from persisted visibility and order.
 * @param {Record<string, boolean>} visibility
 * @param {string[]} order
 * @param {boolean} [allowOriginalEdit]
 * @param {Array<{id: string, label: string}>} [trackOptions] richselect options for the multiview track column
 */
export function buildEventTableColumns(
    visibility = DEFAULT_TABLE_COLUMN_VISIBILITY,
    order = DEFAULT_TABLE_COLUMN_ORDER,
    allowOriginalEdit = false,
    trackOptions = [],
) {
    const vis = { ...DEFAULT_TABLE_COLUMN_VISIBILITY, ...visibility };
    const orderedIds = mergeTableColumnOrder(order);

    /** @type {Record<string, object>} */
    const byId = {
        number: {
            id: "number",
            header: m.editor_table_column_number(),
            width: 64,
            resize: true,
            hidden: !vis.number,
        },
        errors: {
            id: "errors",
            header: m.editor_table_column_errors(),
            width: 40,
            cell: ErrorCell,
            resize: true,
            hidden: !vis.errors,
        },
        track: {
            id: "track",
            header: m.editor_table_column_track(),
            width: 140,
            editor: "richselect",
            options: trackOptions,
            resize: true,
            hidden: !vis.track,
        },
        start: {
            id: "start",
            header: m.editor_table_column_start(),
            width: 130,
            cell: TcCell,
            resize: true,
            hidden: !vis.start,
        },
        end: {
            id: "end",
            header: m.editor_table_column_end(),
            width: 130,
            cell: TcCell,
            resize: true,
            hidden: !vis.end,
        },
        duration: {
            id: "duration",
            header: m.editor_table_column_duration(),
            width: 90,
            resize: true,
            hidden: !vis.duration,
        },
        cps: {
            id: "cps",
            header: m.editor_table_column_cps(),
            width: 64,
            resize: true,
            hidden: !vis.cps,
        },
        alignment: {
            id: "alignment",
            header: m.editor_table_column_align(),
            width: 100,
            cell: AlignCell,
            resize: true,
            hidden: !vis.alignment,
        },
        xPos: {
            id: "xPos",
            header: m.editor_table_column_h(),
            width: 100,
            cell: HPosCell,
            resize: true,
            hidden: !vis.xPos,
        },
        yPos: {
            id: "yPos",
            header: m.editor_table_column_v(),
            width: 100,
            cell: VPosCell,
            resize: true,
            hidden: !vis.yPos,
        },
        style: {
            id: "style",
            header: m.editor_table_column_style(),
            width: 120,
            editor: "richselect",
            options: STYLE_OPTIONS,
            resize: true,
            hidden: !vis.style,
        },
        text: {
            id: "text",
            header: m.editor_table_column_text(),
            flexgrow: 1,
            width: 280,
            cell: QuillTextCell,
            editable: true,
            resize: true,
            hidden: !vis.text,
        },
        original: {
            id: "original",
            header: m.editor_table_column_original(),
            width: 200,
            cell: QuillTextCell,
            textProperty: "original",
            editable: allowOriginalEdit,
            resize: true,
            hidden: !vis.original,
        },
        speakers: {
            id: "speakers",
            header: m.editor_table_column_speakers(),
            width: 140,
            cell: TextCell,
            resize: true,
            hidden: !vis.speakers,
        },
        forced: {
            id: "forced",
            header: m.editor_table_column_forced(),
            width: 90,
            editor: "richselect",
            options: FORCED_OPTIONS,
            resize: true,
            hidden: !vis.forced,
        },
        approved: {
            id: "approved",
            header: m.editor_table_column_approved(),
            width: 120,
            editor: "richselect",
            options: APPROVED_OPTIONS,
            resize: true,
            hidden: !vis.approved,
        },
        notes: {
            id: "notes",
            header: m.editor_table_column_notes(),
            width: 160,
            cell: TextCell,
            editor: "textarea",
            resize: true,
            hidden: !vis.notes,
        },
        wpm: {
            id: "wpm",
            header: m.editor_table_column_wpm(),
            width: 64,
            resize: true,
            hidden: !vis.wpm,
        },
        reply: {
            id: "reply",
            header: m.editor_table_column_reply(),
            width: 160,
            cell: TextCell,
            editor: "textarea",
            resize: true,
            hidden: !vis.reply,
        },

        notesCheck: {
            id: "notesCheck",
            header: m.editor_table_column_notes_check(),
            width: 100,
            cell: CheckCell,
            resize: true,
            hidden: !vis.notesCheck,
        },
        replyCheck: {
            id: "replyCheck",
            header: m.editor_table_column_reply_check(),
            width: 100,
            cell: CheckCell,
            resize: true,
            hidden: !vis.replyCheck,
        },
        voice: {
            id: "voice",
            header: m.editor_table_column_voice(),
            width: 160,
            cell: TextCell,
            resize: true,
            hidden: !vis.voice,
        },
        recipients: {
            id: "recipients",
            header: m.editor_table_column_recipients(),
            width: 140,
            cell: TextCell,
            resize: true,
            hidden: !vis.recipients,
        },
        tags: {
            id: "tags",
            header: m.editor_table_column_tags(),
            width: 160,
            cell: TagsCell,
            resize: true,
            hidden: !vis.tags,
        },
    };

    return orderedIds
        .filter(isTableColumnAllowed)
        .map((id) => byId[id])
        .filter(Boolean);
}
